import ApiService from "./ApiService";
import Logger from "./Logger";

const StatsSlidingService = {
    /**
     * GET /v1/stats/sliding/user-plays?start=&end=&language=&includeTest=false&excludeStaff=true
     * Player activity in a window: distinct players and guest sessions, plays per day, the board
     * sizes / difficulties played, and the most played stories. Start / end are epoch seconds.
     * @returns {Promise<{ totals: Object, daily: Array, byDifficulty: Array, topPuzzles: Array }>}
     */
    userPlayStats: async ({
        start,
        end,
        language = "english",
        includeTest = false,
        excludeStaff = true,
    } = {}) => {
        try {
            // ApiService drops falsy values from the query string — send booleans as explicit strings.
            const params = {
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
            };
            if (start) params.start = start;
            if (end) params.end = end;
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/sliding/user-plays", params);
        } catch (error) {
            Logger.error("Error fetching sliding user play stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/sliding/by-difficulty?language=english
     * Board counts per grid size and difficulty, with per-state and free/paid breakdown.
     * @returns {Promise<{ groups: Array, byState: Object, totalBoards: number, language: string }>}
     */
    statsByDifficulty: async (language = "english") => {
        try {
            const params = {};
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/sliding/by-difficulty", params);
        } catch (error) {
            Logger.error("Error fetching sliding stats by difficulty:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/sliding/tags?language=english
     * Per-tag REVIEWED vs PUBLISHED story-bundle counts, play sessions, and last published time.
     */
    statsByTag: async (language = "english") => {
        try {
            const params = {};
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/sliding/tags", params);
        } catch (error) {
            Logger.error("Error fetching sliding tag stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/sliding/top-users?language=&page=&pageSize=&includeTest=&excludeStaff=&sortField=&sortDir=
     * Signed-in and guest players. sortField: score | completed | updatedAt. sortDir: asc | desc.
     */
    topUsers: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        sortField = "score",
        sortDir = "desc",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                sortField,
                sortDir,
            };
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/sliding/top-users", params);
        } catch (error) {
            Logger.error("Error fetching sliding top users:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/sliding/top-puzzles?language=&page=&pageSize=&includeTest=&excludeStaff=&start=&end=
     * Stories ranked by play count. Omit start/end for all time.
     * Pass editorsPicksOnly=true to limit to editor's-picks stories.
     */
    topPuzzles: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        editorsPicksOnly = false,
        start,
        end,
        sortField = "completedPlays",
        sortDir = "desc",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                editorsPicksOnly: editorsPicksOnly ? "true" : "false",
                sortField,
                sortDir,
            };
            if (language) params.language = language;
            if (start) params.start = start;
            if (end) params.end = end;
            return await ApiService.getData("/v1/stats/sliding/top-puzzles", params);
        } catch (error) {
            Logger.error("Error fetching sliding top puzzles:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/sliding/promotion-users?language=&page=&pageSize=&includeTest=&excludeStaff=&excludeNoEmail=&keyword=
     * Signed-in Sliding Puzzle Quest players with play counts and first/last play times.
     */
    promotionUsers: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        excludeNoEmail = false,
        keyword = "",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                excludeNoEmail: excludeNoEmail ? "true" : "false",
            };
            if (language) params.language = language;
            if (keyword) params.keyword = keyword;
            return await ApiService.getData("/v1/stats/sliding/promotion-users", params);
        } catch (error) {
            Logger.error("Error fetching sliding promotion users:", error);
            throw error;
        }
    },
};

export default StatsSlidingService;
